import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import Skeleton from 'react-loading-skeleton';

import type { VesselMessage } from '../../../../types/VesselMessage';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Text } from '../../../cdl/Text/Text';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';
import { IconX } from '../../../common/icons/cdl/X';
import { Box } from '../../../common/ui/Box';
import { useDemandDefinitionCalculationContext } from '../../hooks/useDemandDefinitionCalculationContext';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';
import type { DemandLeavingVessel } from '../../types/DemandLeavingVessel';
import { DemandVolume } from '../DemandVolume';

interface LeavingVesselCardProps {
    entry: DemandLeavingVessel;
    vessel?: Pick<VesselMessage, 'name' | 'imo'>;
}

export const LeavingVesselCard = ({ entry, vessel }: LeavingVesselCardProps) => {
    const { values, setFieldValue, isSubmitting } = useFormikContext<DemandDefinitionState>();
    const { calculation } = useDemandDefinitionCalculationContext();
    const vesselQuery = useQuery({
        ...queries.vessels.detail(entry.vesselId),
        enabled: vessel === undefined,
    });
    const vesselDetails = vessel ?? vesselQuery.data;
    const demand = calculation?.vessels.find((candidate) => candidate.vesselId === entry.vesselId);
    const excludedVolume = calculation ? demand?.summary.adjustedVolume ?? 0 : undefined;

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={8}
            border="1px solid"
            borderColor="border.muted"
            borderRadius={8}
            backgroundColor="background.default"
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={8}
                paddingX={16}
                paddingTop={16}
            >
                <Text variant="small" fontWeight={600}>
                    {vesselDetails?.name}{' '}
                    {vesselDetails?.imo ? <Text color="foreground.subtle">({vesselDetails.imo})</Text> : null}
                </Text>
                <IconButton
                    type="button"
                    Icon={IconX}
                    size="small"
                    aria-label={translate('demandDefinition.removeLeavingVesselName', {
                        vesselName: vesselDetails?.name ?? '',
                    })}
                    disabled={isSubmitting}
                    onClick={() =>
                        setFieldValue(
                            'leavingVessels',
                            values.leavingVessels.filter((vessel) => vessel.vesselId !== entry.vesselId)
                        )
                    }
                />
            </Box>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={8}
                paddingX={16}
                paddingBottom={16}
            >
                <Text variant="small" color="foreground.default">
                    {translate('demandDefinition.excludedVolume')}
                </Text>
                {excludedVolume === undefined ? (
                    <Skeleton width={80} />
                ) : (
                    <Text variant="small" fontWeight={600}>
                        <DemandVolume volume={excludedVolume} />
                    </Text>
                )}
            </Box>
        </Box>
    );
};
